import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// The gateway module for each course/role
const GATEWAY_MODULES = {
  'security': 'security-privacy-course',
  'curious': 'curious-module-1-welcome',
  'buyer': 'buyer-module-1-welcome',
  'kin': 'kin-module-1-what-is-a-kin',
  'producer': 'producer-module-1-welcome',
  'hub': 'hub-module-1-welcome',
  'pickup': 'pickup-module-1-welcome',
  'educator': 'educator-module-1-welcome'
};

const ROLE_ICONS = {
  'security': '🔒',
  'curious': '🔍',
  'buyer': '🛒',
  'kin': '🌟',
  'producer': '🚜',
  'hub': '🏪',
  'pickup': '📦',
  'educator': '🎓'
};

const ROLE_NAMES = {
  'security': 'Security & Privacy',
  'curious': 'Curious Explorer',
  'buyer': 'Buyer',
  'kin': 'Kin Coordinator',
  'producer': 'Producer',
  'hub': 'Hub',
  'pickup': 'Pickup',
  'educator': 'Educator'
};

const ROLE_DESCRIPTIONS = {
  'security': 'Learn how we protect your privacy and keep your data safe.',
  'curious': 'Discover how Our Pickup can change your relationship with food, money, and community.',
  'buyer': 'Shop fresh, save money, support local farmers.',
  'kin': 'Lead your community, save together with a buying group.',
  'producer': 'Sell direct, keep your margins, know your customers.',
  'hub': 'Aggregate local food, serve your community, build relationships.',
  'pickup': 'Deliver food, earn rewards, be the backbone of the system.',
  'educator': 'Create educational content, share knowledge, earn commissions.'
};

export default function Learn() {
  const { user } = useAuth();
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({});
  
  // Check if user is curious (has completed a course but not registered)
  const isCurious = localStorage.getItem('curious_status') === 'active';
  const curiousEmail = localStorage.getItem('curious_email');

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/education-modules/public', { headers });
      if (response.ok) {
        const data = await response.json();
        
        const moduleMap = {};
        Object.keys(GATEWAY_MODULES).forEach(role => {
          const slug = GATEWAY_MODULES[role];
          const mod = data.find(m => m.slug === slug);
          if (mod) {
            moduleMap[role] = mod;
          }
        });
        setModules(moduleMap);

        if (token) {
          const progressRes = await fetch('/api/education/progress/all', { headers });
          if (progressRes.ok) {
            const progressData = await progressRes.json();
            const progressMap = {};
            progressData.forEach(p => {
              progressMap[p.moduleId] = {
                completed: p.completed,
                score: p.score,
                started: p.started
              };
            });
            setProgress(progressMap);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  const roleOrder = ['security', 'curious', 'buyer', 'kin', 'producer', 'hub', 'pickup', 'educator'];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">🌱 Join the Food Revolution</h1>
      <p className="text-xl text-gray-600 max-w-2xl mb-8">
        Our Pickup connects you directly with local farmers. Fresh food, lower prices, and a stronger community.
      </p>

      {/* Curious Status Banner */}
      {isCurious && !user && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 font-medium">🔍 You're a Curious Explorer!</p>
          <p className="text-yellow-700 text-sm mt-1">
            You've completed courses and earned credits. Register to save your progress permanently.
            {curiousEmail && (
              <span className="block text-xs text-yellow-600 mt-1">
                📧 Progress saved to: {curiousEmail}
              </span>
            )}
          </p>
          <div className="flex flex-wrap gap-3 mt-2">
            <Link
              to="/register"
              className="inline-block bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 text-sm"
            >
              Save Your Progress →
            </Link>
            <button
              onClick={() => {
                localStorage.removeItem('curious_status');
                window.location.reload();
              }}
              className="inline-block text-yellow-600 hover:text-yellow-800 text-sm px-3 py-2"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <p className="text-2xl font-bold text-green-600">30-40%</p>
          <p className="text-sm text-gray-600">Food cost waste eliminated</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <p className="text-2xl font-bold text-blue-600">20-40%</p>
          <p className="text-sm text-gray-600">Average buyer savings</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg text-center">
          <p className="text-2xl font-bold text-purple-600">5%</p>
          <p className="text-sm text-gray-600">Producer selling cost</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg text-center">
          <p className="text-2xl font-bold text-orange-600">10-20%</p>
          <p className="text-sm text-gray-600">Vendor transport savings</p>
        </div>
      </div>

      {/* Find Your Role */}
      <h2 className="text-2xl font-bold mb-4">Find Your Role</h2>
      <p className="text-gray-600 mb-6">
        Everyone can participate in Our Pickup. Choose the role that fits you best.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roleOrder.map(role => {
          const mod = modules[role];
          if (!mod) return null;

          const progressInfo = progress[mod._id];
          const isCompleted = progressInfo?.completed || false;
          const isStarted = progressInfo?.started || false;

          let bgColor = 'bg-white border-gray-200';
          if (role === 'security') bgColor = 'bg-blue-50 border-blue-300';
          else if (role === 'curious') bgColor = 'bg-purple-50 border-purple-300';
          else if (role === 'buyer') bgColor = 'bg-green-50 border-green-300';

          return (
            <div
              key={role}
              className={`border-2 rounded-xl p-6 text-center hover:shadow-lg transition ${bgColor}`}
            >
              <span className="text-5xl block mb-3">{ROLE_ICONS[role]}</span>
              <h3 className="text-xl font-semibold">{ROLE_NAMES[role]}</h3>
              <p className="text-gray-600 mt-2 text-sm">{ROLE_DESCRIPTIONS[role]}</p>

              {mod.reward?.creditAmount > 0 && (
                <p className="text-sm text-green-600 mt-2">
                  💰 Earn {mod.reward.creditAmount} credits
                </p>
              )}

              {isCompleted && (
                <p className="text-sm text-green-600 mt-2">✅ Completed</p>
              )}
              {isStarted && !isCompleted && (
                <p className="text-sm text-blue-600 mt-2">⏳ In progress</p>
              )}

              <Link
                to={`/learn/${mod.slug}`}
                className={`mt-4 inline-block px-4 py-2 rounded text-sm font-medium transition ${
                  isCompleted
                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    : isStarted
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isCompleted ? 'Review Course' : isStarted ? 'Continue' : 'Start Course'}
              </Link>
            </div>
          );
        })}
      </div>

      {/* Call to Action */}
      <div className="mt-12 bg-green-50 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold mb-3">Ready to Get Started?</h2>
        <p className="text-gray-600 mb-6">
          Join Our Pickup today and start saving on fresh, local food.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition">
            Register Free
          </Link>
          <Link
            to="/learn/security-privacy-course"
            className="border border-green-600 text-green-600 px-6 py-2 rounded-lg hover:bg-green-50 transition"
          >
            Start with Security Course
          </Link>
        </div>
      </div>
    </div>
  );
}
